/**
 * catalogue-import — CSV template, parsing and download helpers for Riley's bulk service import.
 *
 * Business logic, deliberately out of the screen: the column contract is the thing an operator
 * fills in a spreadsheet days before they open the app, so it lives in one readable place and the
 * template and the parser are generated from the SAME definition — they cannot drift apart.
 *
 * Validation is NOT here. Rows are validated server-side (serving-engine) against the live
 * catalogue: only the server knows which lines of business, intents and question groups exist, and
 * duplicating those rules client-side would create two answers to one question.
 */

/**
 * One column of the import contract. `example` seeds the sample row in the template.
 *
 * The template is DELIBERATELY TENANT-NEUTRAL (operator, 2026-08-14: "when you download the
 * services template it should not be specific to a tenant"). One file serves a bank, a university
 * and a boutique, so the example row cannot name a bank's products: the vertical-specific cells
 * carry angle-bracket placeholders that say what to put there, and only the vertical-neutral cells
 * (minutes, Y/N flags, windows) carry real values a spreadsheet can keep as-is.
 *
 * `aliases` are older or alternative headers the parser still accepts, so a file built from an
 * earlier download keeps importing after a header is reworded.
 */
export interface ImportColumn { key: string; header: string; required?: boolean; note: string; example: string; aliases?: string[] }

export const IMPORT_COLUMNS: ImportColumn[] = [
  { key: "name", header: "Service name", required: true, note: "What a customer books. Must not already exist.", example: "<service name>" },
  { key: "lob", header: "Line of business", required: true, note: "Name or id of one that already exists in your catalogue. Some organisations call this a department or a division.", example: "<line of business>", aliases: ["Department", "Division", "Service line"] },
  { key: "intent", header: "Intent", required: true, note: "An intent that belongs to that line of business.", example: "<intent>" },
  { key: "description", header: "Description", note: "Optional. Shown to the customer on the journey.", example: "<one line the customer reads>" },
  { key: "durationMin", header: "Appointment minutes", required: true, note: "Whole minutes, above zero.", example: "15" },
  { key: "preMin", header: "Prep minutes", note: "Blocked before the appointment. Defaults to 0.", example: "0" },
  { key: "postMin", header: "Wrap-up minutes", note: "Blocked after the appointment. Defaults to 0.", example: "5" },
  { key: "branch", header: "In person", note: "Y or N — delivered face to face, wherever you deliver it.", example: "Y", aliases: ["In branch", "In store", "On campus"] },
  { key: "phone", header: "Phone", note: "Y or N.", example: "N" },
  { key: "virtual", header: "Virtual", note: "Y or N.", example: "N" },
  { key: "appointments", header: "Bookable as appointment", note: "Y or N. At least one booking method must be Y.", example: "Y" },
  { key: "queueing", header: "Bookable in queue", note: "Y or N.", example: "Y" },
  { key: "events", header: "Bookable as event", note: "Y or N.", example: "N" },
  { key: "questionsAppointments", header: "Questions (appointment)", note: "Name or id of a booking-question group. Blank for none.", example: "<booking-question group>" },
  { key: "questionsQueueing", header: "Questions (queue)", note: "Name or id of a booking-question group.", example: "" },
  { key: "questionsEvents", header: "Questions (event)", note: "Name or id of a booking-question group.", example: "" },
  { key: "bookedBy", header: "Who can book", note: "customer (customers and advisors) or advisor (advisors only). Defaults to customer.", example: "customer" },
  { key: "minAdvance", header: "Earliest booking", note: "Free text, e.g. 2 hours. Blank means not set.", example: "1 hour" },
  { key: "maxAdvance", header: "Latest booking", note: "Free text, e.g. 8 weeks.", example: "4 weeks" },
  { key: "minCancel", header: "Cancellation window", note: "Free text, e.g. 4 hours.", example: "2 hours" },
  { key: "disableBefore", header: "Not bookable before", note: "YYYY-MM-DD. Blank for always available.", example: "" },
  { key: "disableAfter", header: "Not bookable after", note: "YYYY-MM-DD.", example: "" },
  { key: "imageUrl", header: "Image URL", note: "Optional. A hosted https:// address — only that is used; anything else is ignored and the service imports without an image.", example: "" },
  { key: "imageAlt", header: "Image description", note: "Describes the image for screen readers.", example: "" },
];

const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** The template: a header row, one filled example row, then the column guide as comment lines. */
export function importTemplateCsv(): string {
  const header = IMPORT_COLUMNS.map((c) => csvCell(c.header)).join(",");
  const example = IMPORT_COLUMNS.map((c) => csvCell(c.example)).join(",");
  const guide = [
    "",
    "# Delete every line below before importing — they are notes, not data.",
    "# Replace every <placeholder> in the example row with a value from your own catalogue,",
    "# or delete the row and paste your own beneath the header.",
    "# Required columns: " + IMPORT_COLUMNS.filter((c) => c.required).map((c) => c.header).join(", "),
    ...IMPORT_COLUMNS.map((c) => `# ${c.header}${c.required ? " (required)" : ""}: ${c.note}`),
  ];
  return [header, example, ...guide].join("\n") + "\n";
}

/** Split one CSV line, honouring quoted fields and escaped quotes. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "", q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (q && line[i + 1] === '"') { cur += '"'; i++; }
      else q = !q;
    } else if (ch === "," && !q) { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((x) => x.trim());
}

export interface ParsedImport { rows: Record<string, string>[]; unknownHeaders: string[]; missingRequired: string[] }

/* ── Pre-flight validation (operator, 2026-08-04) ─────────────────────────────
 * Checked against the FILE and the live catalogue the screen already holds, before anything is
 * sent, so the operator sees every problem while they can still fix the spreadsheet. The server
 * re-validates the same rules on import — these checks are advisory, never the authority; a client
 * that trusted itself would let a stale catalogue through. */
export interface ImportIssue { line: number; name: string; field: string; problem: string }
export interface ImportRef {
  lobs: { id: string; name: string }[];
  intents: { id: string; name: string; lob: string }[];
  questionGroups: { id: string; name: string }[];
  existingNames: string[];
}
export interface ImportValidation { issues: ImportIssue[]; validRows: Record<string, string>[]; invalidLines: number[] }

const YESNO = new Set(["", "y", "n", "yes", "no", "true", "false", "1", "0"]);
const norm = (s: string) => String(s ?? "").trim();
const lower = (s: string) => norm(s).toLowerCase();
const lineOf = (r: Record<string, string>, idx: number) => {
  const l = (r as Record<string, unknown>).__line;
  return typeof l === "number" ? l : idx + 2;
};

export function validateImportRows(rows: Record<string, string>[], ref: ImportRef): ImportValidation {
  const lobByKey = new Map<string, string>();
  ref.lobs.forEach((l) => { lobByKey.set(lower(l.id), l.id); lobByKey.set(lower(l.name), l.id); });
  const intentByKey = new Map<string, { id: string; lob: string; name: string }>();
  ref.intents.forEach((i) => { intentByKey.set(lower(i.id), i); intentByKey.set(i.lob + "|" + lower(i.name), i); });
  const groupKeys = new Set<string>();
  ref.questionGroups.forEach((g) => { groupKeys.add(lower(g.id)); groupKeys.add(lower(g.name)); });
  const taken = new Set(ref.existingNames.map(lower));

  const issues: ImportIssue[] = [];
  const invalid = new Set<number>();
  const seenInFile = new Map<string, number>();

  rows.forEach((r, idx) => {
    const line = lineOf(r, idx);
    const name = norm(r.name);
    const add = (field: string, problem: string) => { issues.push({ line, name: name || "(no name)", field, problem }); invalid.add(line); };

    if (!name) add("Service name", "Required — this row has no name.");
    else if (taken.has(lower(name))) add("Service name", `A service called "${name}" already exists in the catalogue.`);
    else if (seenInFile.has(lower(name))) add("Service name", `Repeated in this file — also on line ${seenInFile.get(lower(name))}.`);
    else seenInFile.set(lower(name), line);

    const lobId = lobByKey.get(lower(r.lob));
    if (!norm(r.lob)) add("Line of business", "Required.");
    else if (!lobId) add("Line of business", `"${norm(r.lob)}" is not a line of business in this catalogue.`);

    if (!norm(r.intent)) add("Intent", "Required.");
    else if (lobId) {
      const it = intentByKey.get(lower(r.intent)) ?? intentByKey.get(lobId + "|" + lower(r.intent));
      if (!it) add("Intent", `"${norm(r.intent)}" is not an intent in this catalogue.`);
      else if (it.lob !== lobId) add("Intent", `"${it.name}" belongs to a different line of business.`);
    }

    const num = (key: string, label: string, required: boolean) => {
      const raw = norm(r[key]);
      if (!raw) { if (required) add(label, "Required — a whole number of minutes."); return; }
      if (!/^\d+$/.test(raw)) { add(label, `"${raw}" is not a whole number of minutes.`); return; }
      if (required && Number(raw) <= 0) add(label, "Must be above zero.");
      else if (Number(raw) > 480) add(label, `${raw} minutes is over eight hours — check the value.`);
    };
    num("durationMin", "Appointment minutes", true);
    num("preMin", "Prep minutes", false);
    num("postMin", "Wrap-up minutes", false);

    ([["branch", "In branch"], ["phone", "Phone"], ["virtual", "Virtual"], ["appointments", "Bookable as appointment"], ["queueing", "Bookable in queue"], ["events", "Bookable as event"]] as const)
      .forEach(([key, label]) => { if (!YESNO.has(lower(r[key]))) add(label, `"${norm(r[key])}" is not Y or N.`); });
    const yes = (k: string) => ["y", "yes", "true", "1"].includes(lower(r[k]));
    if (!yes("appointments") && !yes("queueing") && !yes("events")) {
      add("Booking method", "Set at least one of appointment / queue / event to Y.");
    }

    ([["questionsAppointments", "Questions (appointment)"], ["questionsQueueing", "Questions (queue)"], ["questionsEvents", "Questions (event)"]] as const)
      .forEach(([key, label]) => {
        const raw = norm(r[key]);
        if (raw && !groupKeys.has(lower(raw))) add(label, `"${raw}" is not a booking-question group.`);
      });

    if (norm(r.bookedBy) && !["customer", "advisor"].includes(lower(r.bookedBy))) {
      add("Who can book", `"${norm(r.bookedBy)}" must be customer or advisor.`);
    }

    const dateOf = (key: string, label: string) => {
      const raw = norm(r[key]);
      if (!raw) return null;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) { add(label, `"${raw}" must be YYYY-MM-DD.`); return null; }
      const d = new Date(raw + "T00:00:00");
      const back = isNaN(d.getTime()) ? "" : [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");
      if (back !== raw) { add(label, `"${raw}" is not a real date.`); return null; }
      return raw;
    };
    const before = dateOf("disableBefore", "Not bookable before");
    const after = dateOf("disableAfter", "Not bookable after");
    if (before && after && before > after) add("Not bookable before", "It falls after the 'not bookable after' date.");

    /* https only. The platform stores a hosted address and refuses anything else, so an
       http:// or data: cell is unusable — flagged here rather than at import, where the row
       lands without an image. */
    const img = norm(r.imageUrl);
    if (img && !/^https:\/\//i.test(img)) {
      add("Image URL", "Must be a hosted https:// address.");
    }
  });

  return {
    issues,
    validRows: rows.filter((r, idx) => !invalid.has(lineOf(r, idx))),
    invalidLines: [...invalid].sort((a, b) => a - b),
  };
}

/**
 * Parse an uploaded CSV into the row shape the import op expects.
 * Header matching is by column HEADER or by key, case- and space-insensitive, so a spreadsheet that
 * has been re-saved or lightly renamed still lands — and each column's `aliases` keep files built
 * from an earlier template, or worded in a tenant's own vocabulary, importing unchanged. `#` comment lines and blank lines are skipped,
 * which is what lets the template ship its own guide inline. Each row keeps its real file line
 * number so a server-side rejection can point at the right row.
 */
export function parseImportCsv(text: string): ParsedImport {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const byHeader = new Map<string, string>();
  IMPORT_COLUMNS.forEach((c) => {
    byHeader.set(norm(c.header), c.key);
    byHeader.set(norm(c.key), c.key);
    (c.aliases ?? []).forEach((a) => byHeader.set(norm(a), c.key));
  });

  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i].trim();
    if (!l || l.startsWith("#")) continue;
    headerIdx = i; break;
  }
  if (headerIdx < 0) return { rows: [], unknownHeaders: [], missingRequired: IMPORT_COLUMNS.filter((c) => c.required).map((c) => c.header) };

  const headers = splitCsvLine(lines[headerIdx]);
  const mapped = headers.map((h) => byHeader.get(norm(h)) ?? null);
  const unknownHeaders = headers.filter((h, i) => h && mapped[i] === null);
  const present = new Set(mapped.filter(Boolean) as string[]);
  const missingRequired = IMPORT_COLUMNS.filter((c) => c.required && !present.has(c.key)).map((c) => c.header);

  const rows: Record<string, string>[] = [];
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw.trim() || raw.trim().startsWith("#")) continue;
    const cells = splitCsvLine(raw);
    if (cells.every((c) => !c)) continue;
    const row: Record<string, string> = {};
    mapped.forEach((key, ci) => { if (key) row[key] = cells[ci] ?? ""; });
    (row as Record<string, unknown>).__line = i + 1;
    rows.push(row);
  }
  return { rows, unknownHeaders, missingRequired };
}

/** Trigger a browser download for text content. */
export function downloadText(filename: string, text: string, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A failure report for one run, so the operator can fix rows without re-reading the screen. */
export function failureReportCsv(runFileName: string, results: { line: number; name: string; ok: boolean; reason?: string }[]): string {
  const rows = results.filter((r) => !r.ok);
  return ["Line,Service name,Why it failed", ...rows.map((r) => [String(r.line), csvCell(r.name), csvCell(r.reason || "")].join(","))].join("\n")
    + `\n# Failures from ${runFileName}\n`;
}

/**
 * Just the rows that have problems, as a re-importable CSV.
 *
 * Deliberately the ORIGINAL lines, verbatim, under the original header — not a rewritten summary.
 * That means the operator fixes the cells and imports the same file again, rather than transcribing
 * corrections back into their source spreadsheet. Each problem is appended as a `#` comment line
 * beneath its row: the parser skips `#` lines, so the annotated file imports without further editing.
 */
export function issueRowsCsv(originalText: string, issues: { line: number; field: string; problem: string }[]): string {
  const lines = originalText.replace(/\r\n?/g, "\n").split("\n");
  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i].trim();
    if (!l || l.startsWith("#")) continue;
    headerIdx = i; break;
  }
  const byLine = new Map<number, string[]>();
  issues.forEach((i) => {
    const arr = byLine.get(i.line) ?? [];
    arr.push(`${i.field}: ${i.problem}`);
    byLine.set(i.line, arr);
  });
  const out: string[] = [];
  if (headerIdx >= 0) out.push(lines[headerIdx]);
  [...byLine.keys()].sort((a, b) => a - b).forEach((line) => {
    const raw = lines[line - 1];
    if (typeof raw === "string") out.push(raw);
    (byLine.get(line) ?? []).forEach((p) => out.push(`# line ${line} — ${p}`));
  });
  out.push("");
  out.push("# Only the rows with problems are included. Fix the cells above, delete these # notes if");
  out.push("# you like (the importer ignores them either way), and import this file again.");
  return out.join("\n") + "\n";
}
